import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Upload, Trash2, Play } from 'lucide-react';
import { useAppStore, type Preset } from '../../store/useAppStore';
import { CollapsiblePanel } from './CollapsiblePanel';
import { triggerDownload } from '../../utils/export';
import './PresetsPanel.css';

export const PresetsPanel: React.FC = () => {
  const { t } = useTranslation(['presets', 'common']);
  const presets = useAppStore(state => state.presets);
  const savePreset = useAppStore(state => state.savePreset);
  const loadPreset = useAppStore(state => state.loadPreset);
  const deletePreset = useAppStore(state => state.deletePreset);
  const importPreset = useAppStore(state => state.importPreset);
  const resetConfig = useAppStore(state => state.resetConfig);

  const [saveName, setSaveName] = useState('');
  const [showSave, setShowSave] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = () => {
    const name = saveName.trim();
    if (!name) {
      setError(t('presets.error.empty', 'Preset name cannot be empty.'));
      return;
    }
    if (presets.some(p => p.name === name)) {
      // Need a confirm dialog. For simplicity using native confirm
      if (!window.confirm(t('presets.confirm.overwrite', 'A preset with this name already exists. Overwrite?'))) {
        return;
      }
    }
    savePreset(name);
    setSaveName('');
    setShowSave(false);
    setError(null);
  };

  const handleExport = (preset: Preset) => {
    const slug = preset.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const filename = `MamaQR-preset-${slug}.json`;
    const blob = new Blob([JSON.stringify(preset, null, 2)], { type: 'application/json' });
    triggerDownload(blob, filename);
  };

  const handleDelete = (name: string) => {
    if (window.confirm(t('presets.confirm.delete', "Delete preset '{{name}}'? This cannot be undone.", { name }))) {
      deletePreset(name);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string) as Partial<Preset>;
        if (!json.version || !json.config) {
          alert(t('presets.error.invalid', 'Invalid preset file. The file may be corrupted or incompatible.'));
          return;
        }
        if (json.version !== 1) {
          alert(t('presets.error.version', 'This preset was created with an incompatible version and cannot be imported.'));
          return;
        }
        if (json.name && presets.some(p => p.name === json.name)) {
          if (!window.confirm(t('presets.confirm.overwriteImport', 'A preset named "{{name}}" already exists. Overwrite?', { name: json.name }))) {
            return;
          }
        }
        importPreset(json as Preset);
      } catch {
        alert(t('presets.error.invalid', 'Invalid preset file. The file may be corrupted or incompatible.'));
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  return (
    <CollapsiblePanel title={t('presets.title', 'Presets')} defaultExpanded={false}>
      <div className="presets-actions">
        {showSave ? (
          <div className="save-form">
            <input 
              type="text" 
              maxLength={50}
              placeholder={t('presets.namePlaceholder', 'Preset Name')}
              value={saveName}
              onChange={e => { setSaveName(e.target.value); }}
              className="preset-input"
            />
            <div className="save-actions">
              <button className="btn-secondary" onClick={() => { setShowSave(false); }}>{t('common:cancel', 'Cancel')}</button>
              <button className="btn-primary" onClick={handleSave} disabled={!saveName.trim()}>{t('common:save', 'Save')}</button>
            </div>
            {error && <div className="field-error">{error}</div>}
          </div>
        ) : (
          <div className="presets-main-actions">
            <button className="btn-secondary" onClick={() => { setShowSave(true); }}>
              {t('presets.saveCurrent', 'Save Current Design')}
            </button>
            <button className="btn-secondary" onClick={handleImportClick}>
              <Upload size={16} />
              {t('presets.import', 'Import JSON')}
            </button>
            <button className="btn-secondary text-error" onClick={() => {
              if (window.confirm(t('presets.confirm.reset', 'Reset all design elements to default?'))) {
                resetConfig();
              }
            }}>
              {t('presets.reset', 'Reset to Defaults')}
            </button>
            <input 
              type="file" 
              accept=".json" 
              className="hidden" 
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </div>
        )}
      </div>

      <div className="presets-list">
        {presets.length === 0 ? (
          <div className="presets-empty">
            {t('presets.empty', 'No saved presets. Save your current design to create one.')}
          </div>
        ) : (
          presets.map(preset => (
            <div key={preset.name} className="preset-item">
              <div className="preset-info">
                <span className="preset-name">{preset.name}</span>
                <span className="preset-date">
                  {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(preset.createdAt ? new Date(preset.createdAt) : new Date())}
                </span>
              </div>
              <div className="preset-item-actions">
                <button className="icon-btn" onClick={() => { loadPreset(preset.name); }} aria-label={t('presets.load', 'Load')}>
                  <Play size={16} className="rtl-flip" />
                </button>
                <button className="icon-btn" onClick={() => { handleExport(preset); }} aria-label={t('presets.export', 'Export')}>
                  <Download size={16} />
                </button>
                <button className="icon-btn text-error" onClick={() => { handleDelete(preset.name); }} aria-label={t('presets.delete', 'Delete')}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </CollapsiblePanel>
  );
};
